import dayjs from "lib/dayjs";
import {
  addSectionTitle,
  ensurePageSpace,
  PDF_CONTENT_GAP,
} from "reporting/pdf/pdfLayout";
import { PDF_CHART_SERIES, PDF_STONE } from "reporting/pdf/pdfTheme";

const COLORS = {
  primary: PDF_CHART_SERIES[0],
  secondary: PDF_CHART_SERIES[1],
  grid: PDF_STONE.grid,
  axis: PDF_STONE.axis,
};

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatKmb(value) {
  const n = num(value);
  const absolute = Math.abs(n);
  if (absolute >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (absolute >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (absolute >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return `${Math.round(n)}`;
}

function niceMax(value) {
  const n = num(value);
  if (n <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(n));
  const normalized = n / magnitude;
  const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return factor * magnitude;
}

function drawLegendItem(doc, x, y, color, label, line = false) {
  doc.setDrawColor(...color);
  doc.setFillColor(...color);
  if (line) {
    doc.setLineWidth(1.5);
    doc.line(x, y - 4, x + 14, y - 4);
    doc.circle(x + 7, y - 4, 1.5, "F");
  } else {
    doc.rect(x, y - 9, 12, 7, "F");
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(70);
  doc.text(label, x + 18, y - 3);
}

function drawChartFrame(ctx, title, startY, data, maxValue) {
  const { doc, margins, usableWidth } = ctx;
  const chartHeight = 230;
  let y = ensurePageSpace(ctx, startY, chartHeight + 42);
  y = addSectionTitle(ctx, title, y);

  if (!data.length) {
    doc.setFontSize(8);
    doc.setTextColor(90);
    doc.text("No chart data available.", margins.left, y);
    return { empty: true, endY: y + PDF_CONTENT_GAP };
  }

  const frame = {
    x: margins.left,
    y,
    width: usableWidth,
    height: chartHeight,
    left: margins.left + 48,
    right: margins.left + usableWidth - 12,
    top: y + 8,
    bottom: y + chartHeight - 44,
  };
  const yMax = niceMax(maxValue * 1.05);
  const yPos = (value) => frame.bottom - (Math.max(0, num(value)) / yMax) * (frame.bottom - frame.top);

  doc.setDrawColor(215);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(frame.x, frame.y, frame.width, frame.height, 5, 5, "S");

  for (let index = 0; index < 5; index += 1) {
    const value = (yMax * index) / 4;
    const yy = yPos(value);
    doc.setDrawColor(...COLORS.grid);
    doc.setLineWidth(0.35);
    doc.line(frame.left, yy, frame.right, yy);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(85);
    doc.text(`$${formatKmb(value)}`, frame.left - 7, yy + 2.5, { align: "right" });
  }
  doc.setDrawColor(...COLORS.axis);
  doc.setLineWidth(0.6);
  doc.line(frame.left, frame.bottom, frame.right, frame.bottom);
  doc.line(frame.left, frame.top, frame.left, frame.bottom);

  const slot = (frame.right - frame.left) / data.length;
  const xPos = (index) => frame.left + slot * index + slot / 2;
  const labelEvery = Math.max(1, Math.ceil(data.length / 10));
  data.forEach((row, index) => {
    if (index % labelEvery && index !== data.length - 1) return;
    const parsed = dayjs(row.date);
    const label = parsed.isValid() ? parsed.format("M/YY") : String(row.date || "");
    doc.setFontSize(6.5);
    doc.setTextColor(85);
    doc.text(label, xPos(index), frame.bottom + 12, { align: "center" });
  });

  return {
    empty: false,
    frame,
    yPos,
    xPos,
    slot,
    endY: frame.y + frame.height + PDF_CONTENT_GAP,
  };
}

export function addStructurerScheduleChart(ctx, rows = [], startY) {
  const data = rows.filter((row) => num(row?.requirement) !== 0);
  const chart = drawChartFrame(
    ctx,
    "Requirements",
    startY,
    data,
    Math.max(...data.map((row) => num(row.requirement)), 1)
  );
  if (chart.empty) return chart.endY;
  const { doc } = ctx;
  const barWidth = Math.max(2, Math.min(18, chart.slot * 0.58));
  data.forEach((row, index) => {
    const top = chart.yPos(row.requirement);
    doc.setFillColor(...COLORS.primary);
    doc.rect(chart.xPos(index) - barWidth / 2, top, barWidth, chart.frame.bottom - top, "F");
  });
  drawLegendItem(doc, chart.frame.left, chart.frame.y + chart.frame.height - 13, COLORS.primary, "Cash Flow Requirement");
  return chart.endY;
}

export function addStructurerSufficiencyChart(
  ctx,
  rows = [],
  startY,
  fundMode = "net"
) {
  const isGrossFund = fundMode === "gross";
  const data = rows.filter((row) =>
    isGrossFund
      ? num(row?.availableBalance) > 0
      : num(row?.drawAmount) !== 0 || num(row?.balance) !== 0
  );
  const chart = drawChartFrame(
    ctx,
    "Fund Cash Flows",
    startY,
    data,
    Math.max(
      ...data.map((row) =>
        isGrossFund
          ? num(row.availableBalance)
          : Math.max(num(row.drawAmount), num(row.balance))
      ),
      1
    )
  );
  if (chart.empty) return chart.endY;
  const { doc } = ctx;
  const barWidth = Math.max(2, Math.min(16, chart.slot * 0.45));

  data.forEach((row, index) => {
    const value = isGrossFund
      ? num(row.availableBalance)
      : num(row.drawAmount);
    if (value > 0) {
      const top = chart.yPos(value);
      doc.setFillColor(...COLORS.primary);
      doc.rect(
        chart.xPos(index) - barWidth / 2,
        top,
        barWidth,
        chart.frame.bottom - top,
        "F"
      );
    }
  });

  const legendY = chart.frame.y + chart.frame.height - 13;
  drawLegendItem(
    doc,
    chart.frame.left,
    legendY,
    COLORS.primary,
    isGrossFund ? "Available Balance" : "Fund Withdrawals"
  );

  if (!isGrossFund) {
    const points = data.map((row, index) => ({
      x: chart.xPos(index),
      y: chart.yPos(row.balance),
    }));
    doc.setDrawColor(...COLORS.secondary);
    doc.setLineWidth(1.6);
    for (let index = 1; index < points.length; index += 1) {
      doc.line(
        points[index - 1].x,
        points[index - 1].y,
        points[index].x,
        points[index].y
      );
    }
    doc.setFillColor(...COLORS.secondary);
    points.forEach((point) => doc.circle(point.x, point.y, 1.6, "F"));
    drawLegendItem(
      doc,
      chart.frame.left + 90,
      legendY,
      COLORS.secondary,
      "Fund Balance",
      true
    );
  }

  return chart.endY;
}
